"use client";

import { ArrowLeft, ArrowRight } from "iconoir-react";
import { useState } from "react";

import { SmoothHashLink } from "@/components/customers/smooth-hash-link";
import { PreviewVideo } from "@/components/ui/preview-video";
import { cn } from "@/lib/utils";

export interface CaseStudyItem {
  slug: string;
  title: string;
  playbackId: string;
  poster: string;
  challenge: string;
  solution: string;
  outcome: string;
}

interface CaseStudiesCarouselProps {
  eyebrow: string;
  items: CaseStudyItem[];
  labels: {
    challenge: string;
    solution: string;
    outcome: string;
    previous: string;
    next: string;
    seeAll: string;
  };
  seeAllHref: string;
  className?: string;
}

// Customers page — Case studies carousel (Figma desktop 12364:97311 / mobile
// 12364:97461). One story at a time: headline, Challenge / Solution / Outcome
// copy and the customer video, with previous/next controls.
export function CaseStudiesCarousel({
  eyebrow,
  items,
  labels,
  seeAllHref,
  className,
}: CaseStudiesCarouselProps) {
  const [index, setIndex] = useState(0);
  const active = items[index];
  if (!active) return null;

  const hasMany = items.length > 1;
  const go = (delta: number) =>
    setIndex((current) => (current + delta + items.length) % items.length);

  const steps = [
    { label: labels.challenge, body: active.challenge },
    { label: labels.solution, body: active.solution },
    { label: labels.outcome, body: active.outcome },
  ];

  return (
    <section
      data-testid="our-customers-case-studies"
      className={cn("bg-grey-50 lg:px-20", className)}
    >
      <div className="bg-grey-0 border-grey-100 mx-auto flex max-w-[1280px] flex-col gap-8 px-6 py-8 lg:border-x lg:border-b lg:px-[85px] lg:pt-[60px] lg:pb-[85px]">
        <div className="flex items-start justify-between gap-6">
          <div className="flex max-w-[641px] flex-col gap-2">
            <p className="text-ds-body-xs text-grey-500">{eyebrow}</p>
            <h2 className="text-ds-heading-h4 lg:text-ds-heading-h2 text-grey-950">
              {active.title}
            </h2>
          </div>
          {hasMany ? (
            <div className="hidden gap-4 lg:flex">
              <CarouselButton label={labels.previous} onClick={() => go(-1)}>
                <ArrowLeft className="size-3" aria-hidden />
              </CarouselButton>
              <CarouselButton label={labels.next} onClick={() => go(1)}>
                <ArrowRight className="size-3" aria-hidden />
              </CarouselButton>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
          <div className="order-2 flex flex-col gap-6 lg:order-1 lg:w-[326px] lg:shrink-0">
            {steps.map((step, stepIndex) => (
              <div key={step.label} className="flex flex-col gap-3">
                <p className="text-ds-body-sm text-grey-950 flex items-center gap-1.5">
                  <span className="opacity-20">
                    {String(stepIndex + 1).padStart(2, "0")}
                  </span>
                  {step.label}
                </p>
                <p className="text-ds-body-sm text-grey-500">{step.body}</p>
              </div>
            ))}
            <SmoothHashLink
              href={seeAllHref}
              className="text-ds-body-xs text-grey-500 hover:text-grey-950 font-medium"
            >
              {labels.seeAll}
            </SmoothHashLink>
          </div>

          <div className="order-1 flex flex-col gap-5 lg:order-2 lg:w-[674px] lg:shrink-0">
            <PreviewVideo
              key={active.playbackId}
              playbackId={active.playbackId}
              title={active.title}
              poster={active.poster}
            />
            {hasMany ? (
              <div className="flex gap-4 lg:hidden">
                <CarouselButton
                  label={labels.previous}
                  onClick={() => go(-1)}
                  className="flex-1"
                >
                  <ArrowLeft className="size-3" aria-hidden />
                </CarouselButton>
                <CarouselButton
                  label={labels.next}
                  onClick={() => go(1)}
                  className="flex-1"
                >
                  <ArrowRight className="size-3" aria-hidden />
                </CarouselButton>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function CarouselButton({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "border-grey-100 bg-grey-50 text-grey-950 hover:bg-grey-100 focus-visible:ring-pulse-700 rounded-ds-m flex h-9 min-w-9 items-center justify-center border focus-visible:ring-2 focus-visible:outline-none",
        className
      )}
    >
      {children}
    </button>
  );
}
